import { toolCallStore, type ChatCardStage } from "@/shared/ui/chat-card/tool-call-store";
import {
  chatCardClient,
  chatCardSeed,
  isRecord,
  type ChatCardClient,
  type ChatCardSeed,
} from "@/features/portfolio-analysis/lib/chat-card-client";

export type RiskExplanationSeed = ChatCardSeed;

export interface RiskPortfolio {
  id: string;
  name: string;
}

export interface RiskExplanationEntry {
  toolCallId: string;
  seed: RiskExplanationSeed;
  client: ChatCardClient | null;
  portfolio: RiskPortfolio | null;
}

export function openRiskExplanation(toolCallId: string, seed: RiskExplanationSeed): void {
  STORE.open({ toolCallId, seed, client: null, portfolio: null });
}

export function riskExplanationAnswered(toolCallId: string): void {
  STORE.answered(toolCallId);
}

export function hasRiskExplanation(toolCallId: string): boolean {
  return STORE.has(toolCallId);
}

export function useRiskExplanation(toolCallId: string): RiskExplanationEntry | null {
  return STORE.useEntry(toolCallId);
}

export function useRiskExplanationAnswering(toolCallId: string): boolean {
  return STORE.useAnswering(toolCallId);
}

export function useRiskExplanationStages(): (toolCallId: string) => ChatCardStage | null {
  return STORE.useStages();
}

export function chooseRiskClient(toolCallId: string, client: ChatCardClient): void {
  STORE.update(toolCallId, (entry) => ({ ...entry, client, portfolio: null }));
}

export function chooseRiskPortfolio(
  toolCallId: string,
  client: ChatCardClient,
  portfolio: RiskPortfolio,
): void {
  const known = STORE.read(toolCallId);
  if (
    known?.portfolio?.id === portfolio.id &&
    known.client?.kind === client.kind &&
    known.client.subjectId === client.subjectId
  ) {
    return;
  }
  STORE.update(toolCallId, (entry) => ({
    ...entry,
    client,
    portfolio: { id: portfolio.id, name: portfolio.name },
  }));
}

function narrowEntry(value: unknown): RiskExplanationEntry | null {
  if (!isRecord(value) || typeof value.toolCallId !== "string") return null;
  const client = chatCardClient(value.client);
  return {
    toolCallId: value.toolCallId,
    seed: chatCardSeed(value.seed),
    client,
    portfolio: client === null ? null : portfolioOf(value.portfolio),
  };
}

function portfolioOf(value: unknown): RiskPortfolio | null {
  if (!isRecord(value)) return null;
  const { id, name } = value;
  if (typeof id !== "string" || id === "" || typeof name !== "string") return null;
  return { id, name };
}

const STORE = toolCallStore<RiskExplanationEntry>({
  key: "simon.analysis.riskExplanation",
  narrow: narrowEntry,
  stage: (entry) => (entry.portfolio === null ? "awaiting" : "done"),
});
